import { AxiosError } from 'axios';
import { resolveTenantFromUrl } from '@low6-pickem/frontend-shared';

/**
 * Skypicks restricts pick submission to its own Wi-Fi network, so a 403 there
 * means a network/VPN restriction rather than a generic permission failure.
 */
const SKY_FORBIDDEN_MESSAGE =
  'Picks can only be initially submitted while connected to Delta Sync Wi-Fi. VPNs and iCloud Private Relay must be disabled to submit picks.';

/** True when the active tenant is Skypicks. */
function isSkyTenant(): boolean {
  return resolveTenantFromUrl() === 'sky';
}

/**
 * Error categories for API errors
 */
export enum ErrorCategory {
  /** Network connectivity issues */
  NETWORK = 'NETWORK',
  /** Authentication/authorization failures */
  AUTH = 'AUTH',
  /** Validation errors (400, 422) */
  VALIDATION = 'VALIDATION',
  /** Server errors (500, 502, 503) */
  SERVER = 'SERVER',
  /** Unknown or unhandled errors */
  UNKNOWN = 'UNKNOWN',
}

/**
 * Custom API error class with categorization
 *
 * Extends Error to provide structured error information
 * for better error handling and user messaging.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public category: ErrorCategory,
    public statusCode?: number,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Categorizes an error based on its type and status code
 *
 * @param error - The error to categorize (unknown type)
 * @returns An ApiError with appropriate category
 *
 * @example
 * ```ts
 * try {
 *   await apiCall();
 * } catch (error) {
 *   const apiError = categorizeError(error);
 *   console.log(apiError.category); // 'NETWORK', 'AUTH', etc.
 * }
 * ```
 */
export function categorizeError(error: unknown): ApiError {
  // Handle AxiosError
  if (error instanceof AxiosError) {
    const statusCode = error.response?.status;

    // Network errors (no response)
    if (!error.response) {
      return new ApiError(
        'Network error. Please check your connection.',
        ErrorCategory.NETWORK,
        undefined,
        error
      );
    }

    // Authentication errors (401, 403)
    if (statusCode === 401 || statusCode === 403) {
      const forbiddenMessage = isSkyTenant()
        ? SKY_FORBIDDEN_MESSAGE
        : 'You do not have permission to access this resource.';
      return new ApiError(
        statusCode === 401 ? 'Please log in to continue.' : forbiddenMessage,
        ErrorCategory.AUTH,
        statusCode,
        error
      );
    }

    // Validation errors (400, 422)
    if (statusCode === 400 || statusCode === 422) {
      const message = error.response?.data?.message || 'Invalid request. Please check your input.';
      return new ApiError(message, ErrorCategory.VALIDATION, statusCode, error);
    }

    // Server errors (500, 502, 503, 504)
    if (statusCode && statusCode >= 500) {
      return new ApiError(
        'Server error. Please try again later.',
        ErrorCategory.SERVER,
        statusCode,
        error
      );
    }

    // Other HTTP errors
    return new ApiError(
      error.response?.data?.message || error.message || 'An error occurred.',
      ErrorCategory.UNKNOWN,
      statusCode,
      error
    );
  }

  // Handle ApiError instances (already categorized)
  if (error instanceof ApiError) {
    return error;
  }

  // Handle generic Error instances
  if (error instanceof Error) {
    return new ApiError(error.message, ErrorCategory.UNKNOWN, undefined, error);
  }

  // Handle unknown error types
  return new ApiError('An unexpected error occurred.', ErrorCategory.UNKNOWN, undefined, error);
}

/**
 * Get user-friendly error message based on error category
 *
 * @param error - The ApiError to get message for
 * @returns User-friendly error message
 *
 * @example
 * ```ts
 * const apiError = categorizeError(error);
 * const message = getUserFriendlyMessage(apiError);
 * toast({ variant: 'destructive', description: message });
 * ```
 */
export function getUserFriendlyMessage(error: ApiError): string {
  // If error already has a specific message, use it
  if (error.message && error.message !== 'An unexpected error occurred.') {
    return error.message;
  }

  // Provide category-specific fallback messages
  switch (error.category) {
    case ErrorCategory.NETWORK:
      return 'Unable to connect. Please check your internet connection and try again.';

    case ErrorCategory.AUTH:
      if (error.statusCode === 401) {
        return 'Your session has expired. Please log in again.';
      }
      return isSkyTenant()
        ? SKY_FORBIDDEN_MESSAGE
        : 'You do not have permission to perform this action.';

    case ErrorCategory.VALIDATION:
      return 'Please check your input and try again.';

    case ErrorCategory.SERVER:
      return 'Our servers are experiencing issues. Please try again in a few moments.';

    case ErrorCategory.UNKNOWN:
    default:
      return 'Something went wrong. Please try again.';
  }
}
